import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useApp } from '../context/AppContext';

const authHeaders = token => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

export default function Inbox({ toast }) {
  const { token } = useApp();
  const [threads, setThreads]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [thread, setThread]       = useState(null);
  const [replyBody, setReplyBody] = useState('');
  const [sending, setSending]     = useState(false);
  const [filter, setFilter]       = useState('');
  const [search, setSearch]       = useState('');
  const [total, setTotal]         = useState(0);
  const [polling, setPolling]     = useState(false);
  const [page, setPage]           = useState(1);
  const LIMIT = 30;
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filter) qs.set('status', filter);
      if (search) qs.set('search', search);
      qs.set('page', page);
      qs.set('limit', LIMIT);
      const res = await fetch(`/api/inbox?${qs}`, { headers: authHeaders(token) });
      const data = await res.json();
      setThreads(data.data || []);
      setTotal(data.pagination?.total || 0);
    } catch(e) {} finally { setLoading(false); }
  }, [token, filter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filter, search]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [thread]);

  const openThread = async (id) => {
    setSelected(id);
    try {
      const res = await fetch(`/api/inbox/${id}`, { headers: authHeaders(token) });
      const data = await res.json();
      setThread(data.data || data);
      setThreads(prev => prev.map(t => t.id === id ? { ...t, unread: false } : t));
    } catch(e) {}
  };

  const sendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`/api/inbox/${selected}/reply`, {
        method: 'POST', headers: authHeaders(token),
        body: JSON.stringify({ body: replyBody }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed');
      setThread(data.data?.thread || data.data);
      setReplyBody('');
      toast?.('Reply sent ✅');
      load();
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSending(false); }
  };

  const markStatus = async (id, status) => {
    try {
      await fetch(`/api/inbox/${id}/status`, {
        method: 'PATCH', headers: authHeaders(token),
        body: JSON.stringify({ status }),
      });
      setThreads(prev => prev.map(t => t.id === id ? { ...t, status } : t));
      if (thread?.id === id) setThread(prev => ({ ...prev, status }));
      toast?.(`Thread ${status.toLowerCase()}`);
    } catch(e) {}
  };

  const pollImap = async () => {
    setPolling(true);
    try {
      const res = await fetch('/api/inbox/poll', { method: 'POST', headers: authHeaders(token) });
      const data = await res.json();
      toast?.(data.data?.message || 'Checked mail');
      load();
    } catch(e) {}
    finally { setPolling(false); }
  };

  const unread = threads.filter(t => t.unread).length;

  const formatTime = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const formatMsgTime = (ts) => new Date(ts).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });

  return (
    <div style={{ display:'flex', height:'calc(100vh - 100px)', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10, overflow:'hidden' }}>

      {/* ── Left: Thread list ── */}
      <div style={{ width:320, borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', background:'var(--bg-page)' }}>

        {/* Search + poll */}
        <div style={{ padding:'12px 14px', borderBottom:'1px solid var(--border)', background:'var(--bg-card)' }}>
          <div style={{ display:'flex', gap:6, marginBottom:8 }}>
            <input className="search-input" placeholder="Search…" value={search}
              onChange={e => setSearch(e.target.value)} style={{ flex:1, fontSize:12 }} />
            <button onClick={pollImap} disabled={polling} title="Check for new replies"
              style={{ background:'var(--accent)', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:14 }}>
              {polling ? '⟳' : '🔄'}
            </button>
          </div>
          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
            {['','Open','Closed'].map(s => (
              <button key={s} onClick={() => setFilter(s)}
                style={{ padding:'3px 10px', borderRadius:20, fontSize:11, border:'none', cursor:'pointer',
                  background: filter===s ? 'var(--accent)' : 'var(--bg-input)',
                  color: filter===s ? '#0a0a0a' : 'var(--text-dim)', fontWeight: filter===s ? 600 : 400 }}>
                {s || 'All'}
              </button>
            ))}
            {unread > 0 && (
              <span style={{ marginLeft:'auto', background:'#25D366', color:'#fff', borderRadius:20, padding:'2px 7px', fontSize:10, fontWeight:700 }}>
                {unread}
              </span>
            )}
          </div>
        </div>

        {/* Thread list */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:32, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading…</div>
          ) : threads.length === 0 ? (
            <div style={{ padding:32, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
              <div style={{ fontSize:40, marginBottom:8 }}>📭</div>
              <div style={{ fontWeight:500 }}>No threads yet</div>
              <div style={{ fontSize:12, marginTop:4 }}>Replies appear here automatically</div>
            </div>
          ) : threads.map(t => (
            <div key={t.id} onClick={() => openThread(t.id)}
              style={{ padding:'12px 14px', cursor:'pointer', borderBottom:'1px solid var(--border)',
                background: selected===t.id ? 'rgba(167,244,50,0.06)' : 'transparent',
                transition:'background 0.15s' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3, alignItems:'center' }}>
                <div style={{ fontSize:12, fontWeight: t.unread ? 700 : 500, color:'var(--text-primary)',
                  overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:200 }}>
                  {t.contact.replace(/"([^"]+)".*/, '$1').replace(/<[^>]+>/, '').trim() || t.contact}
                </div>
                <div style={{ fontSize:10, color:'var(--text-dim)', flexShrink:0, marginLeft:4 }}>
                  {formatTime(t.updatedAt)}
                </div>
              </div>
              <div style={{ fontSize:12, color: t.unread ? 'var(--text-primary)' : 'var(--text-muted)',
                marginBottom:2, fontWeight: t.unread ? 600 : 400,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                {t.subject}
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div style={{ fontSize:11, color:'var(--text-dim)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>
                  {t.preview}
                </div>
                {t.unread && (
                  <div style={{ width:8, height:8, borderRadius:'50%', background:'var(--accent)', flexShrink:0, marginLeft:6 }} />
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {total > LIMIT && (
          <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--bg-card)' }}>
            <button
              onClick={() => setPage(p => Math.max(1, p-1))}
              disabled={page === 1}
              style={{ background:'var(--bg-input)', border:'none', borderRadius:6, padding:'5px 12px', cursor: page===1 ? 'default' : 'pointer', color: page===1 ? 'var(--text-dim)' : 'var(--text-primary)', fontSize:12 }}>
              ← Prev
            </button>
            <span style={{ fontSize:11, color:'var(--text-dim)' }}>
              {((page-1)*LIMIT)+1}–{Math.min(page*LIMIT, total)} of {total}
            </span>
            <button
              onClick={() => setPage(p => p+1)}
              disabled={page*LIMIT >= total}
              style={{ background:'var(--bg-input)', border:'none', borderRadius:6, padding:'5px 12px', cursor: page*LIMIT>=total ? 'default' : 'pointer', color: page*LIMIT>=total ? 'var(--text-dim)' : 'var(--text-primary)', fontSize:12 }}>
              Next →
            </button>
          </div>
        )}
      </div>

      {/* ── Right: Conversation ── */}}
      <div style={{ flex:1, display:'flex', flexDirection:'column',
        backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(167,244,50,0.03) 1px, transparent 0)',
        backgroundSize: '24px 24px' }}>

        {!thread ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:'var(--text-dim)' }}>
            <div style={{ fontSize:64 }}>💬</div>
            <div style={{ fontWeight:600, fontSize:16, color:'var(--text-primary)' }}>Select a conversation</div>
            <div style={{ fontSize:13 }}>Choose a thread from the left to view messages</div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div style={{ padding:'12px 18px', borderBottom:'1px solid var(--border)', background:'var(--bg-card)',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:40, height:40, borderRadius:'50%', background:'var(--accent)',
                  display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#0a0a0a' }}>
                  {(thread.contact.replace(/"([^"]+)".*/, '$1').trim()[0] || '?').toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight:600, fontSize:13, color:'var(--text-primary)' }}>
                    {thread.contact.replace(/"([^"]+)".*/, '$1').replace(/<[^>]+>/g, '').trim() || thread.contact}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-dim)' }}>
                    {thread.subject}
                  </div>
                </div>
              </div>
              <button className="btn btn-ghost" style={{ fontSize:11 }}
                onClick={() => markStatus(thread.id, thread.status==='Open' ? 'Closed' : 'Open')}>
                {thread.status==='Open' ? '✓ Close' : '↩ Reopen'}
              </button>
            </div>

            {/* Messages */}
            <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:8 }}>
              {(thread.messages||[]).length === 0 ? (
                <div style={{ textAlign:'center', padding:32, color:'var(--text-dim)', fontSize:13 }}>No messages</div>
              ) : (thread.messages||[]).map((msg, i) => {
                const isOut = msg.type === 'outbound';
                return (
                  <div key={msg.id||i} style={{ display:'flex', flexDirection:'column',
                    alignItems: isOut ? 'flex-end' : 'flex-start', marginBottom:2 }}>
                    <div style={{ maxWidth:'70%' }}>
                      <div style={{
                        padding:'8px 12px',
                        borderRadius: isOut ? '12px 2px 12px 12px' : '2px 12px 12px 12px',
                        background: isOut ? 'rgba(167,244,50,0.15)' : 'var(--bg-input)',
                        border: `1px solid ${isOut ? 'rgba(167,244,50,0.3)' : 'var(--border)'}`,
                        fontSize: 13, color:'var(--text-primary)', lineHeight:1.5, whiteSpace:'pre-wrap',
                        wordBreak:'break-word',
                      }}>
                        {msg.body}
                      </div>
                      <div style={{ fontSize:10, color:'var(--text-dim)', marginTop:3,
                        textAlign: isOut ? 'right' : 'left', paddingLeft: isOut ? 0 : 4, paddingRight: isOut ? 4 : 0 }}>
                        {isOut ? '📤 TransferExpo' : '📥 ' + (thread.contact.replace(/"([^"]+)".*/, '$1').trim() || thread.contact)}
                        {' · '}{formatMsgTime(msg.timestamp)}
                        {isOut && <span style={{ marginLeft:4, color:'var(--accent)' }}>✓✓</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Reply box */}
            {thread.status === 'Open' ? (
              <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', background:'var(--bg-card)',
                display:'flex', gap:8, alignItems:'flex-end' }}>
                <textarea value={replyBody} onChange={e => setReplyBody(e.target.value)}
                  placeholder="Type a message…"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); }}}
                  rows={2}
                  style={{ flex:1, background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:20,
                    color:'var(--text-primary)', fontSize:13, padding:'9px 14px',
                    resize:'none', fontFamily:'inherit', lineHeight:1.5, outline:'none' }} />
                <button onClick={sendReply} disabled={sending || !replyBody.trim()}
                  style={{ width:42, height:42, borderRadius:'50%', background: replyBody.trim() ? 'var(--accent)' : 'var(--bg-input)',
                    border:'none', cursor: replyBody.trim() ? 'pointer' : 'default',
                    fontSize:18, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'background 0.2s' }}>
                  {sending ? '⟳' : '➤'}
                </button>
              </div>
            ) : (
              <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)', background:'var(--bg-card)',
                textAlign:'center', fontSize:12, color:'var(--text-dim)' }}>
                Thread closed — <span style={{ color:'var(--accent)', cursor:'pointer' }}
                  onClick={() => markStatus(thread.id, 'Open')}>Reopen to reply</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
